import React, { useState, useEffect, useRef } from 'react';
import { findShipmentByTrackingNumber, updateShipmentPickupDatetime } from '../utils/odoo-api';
import { createDeliveryScanBatch, recordBatchScan, getBatchScans, uploadMultipleImages } from '../utils/supabase';
import { Html5Qrcode } from 'html5-qrcode';
import ImageCapture from './ImageCapture';
import './DeliveryTracker.css';

const DeliveryTracker = ({ userId }) => {
  // State for tracking form and scan results
  const [trackingNumber, setTrackingNumber] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [scanResults, setScanResults] = useState([]);
  const [stats, setStats] = useState({
    totalScans: 0,
    successfulScans: 0,
    errorScans: 0
  });
  const [notification, setNotification] = useState(null);
  
  // State for camera scanning
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [availableCameras, setAvailableCameras] = useState([]);
  const [scannerError, setScannerError] = useState(false);
  const scannerRef = useRef(null);
  const scannerContainerRef = useRef(null);
  
  // State for batch management - sin diálogo para optimización en PDA
  const [currentBatch, setCurrentBatch] = useState(null);
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);
  
  // State for image capture
  const [capturedImages, setCapturedImages] = useState([]);
  
  // Crear automáticamente un lote al cargar el componente
  useEffect(() => {
    if (!currentBatch && !isCreatingBatch) {
      // Establecer bandera para evitar múltiples intentos
      setIsCreatingBatch(true);
      
      // Crear un nombre de lote basado en la fecha y hora actual
      const now = new Date();
      const formattedDate = now.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).replace(/\//g, '-');
      
      const formattedTime = now.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/:/g, '-');
      
      const batchName = `Entregas_${formattedDate}_${formattedTime}`;
      
      console.log(`Creando lote automático: ${batchName}`);
      
      // Crear el lote
      createDeliveryScanBatch(batchName)
        .then(batch => {
          setCurrentBatch(batch);
          setIsCreatingBatch(false);
          
          // Mostrar notificación
          setNotification({
            type: 'success',
            message: `Lote "${batchName}" creado automáticamente`
          });
        })
        .catch(error => {
          console.error("Error al crear lote automático:", error);
          setIsCreatingBatch(false);
          
          // Mostrar error
          setNotification({
            type: 'error',
            message: `Error al crear lote: ${error.message}`
          });
        });
    }
  }, [currentBatch, isCreatingBatch]);
  
  // Load available cameras and select the rear-facing camera by default if available
  useEffect(() => {
    const loadCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setAvailableCameras(videoDevices);
        
        if (videoDevices.length > 0) {
          // Try to find rear-facing camera by label (common patterns in camera names)
          const rearCameraPatterns = [
            /back/i, /rear/i, /trasera/i, /traseira/i, /posterior/i, /atrás/i, 
            /environment/i, /0,0/
          ];
          
          // Check if any camera matches our patterns for rear cameras
          const rearCamera = videoDevices.find(device => {
            if (device.label) {
              return rearCameraPatterns.some(pattern => pattern.test(device.label));
            }
            return false;
          });
          
          if (rearCamera) {
            console.log(`Found rear camera: ${rearCamera.label}`);
            setSelectedCamera(rearCamera.deviceId);
          } else {
            // If we couldn't identify the rear camera, use the last camera in the list
            // (mobile devices often list the rear camera last)
            const lastCamera = videoDevices[videoDevices.length - 1];
            console.log(`Using last camera as fallback: ${lastCamera.label || 'Unnamed camera'}`);
            setSelectedCamera(lastCamera.deviceId);
          }
        }
      } catch (error) {
        console.error("Error loading cameras:", error);
      }
    };
    
    // Request camera permissions and load available devices
    navigator.mediaDevices.getUserMedia({ 
      video: { 
        facingMode: { ideal: "environment" } // Prefer rear camera
      } 
    })
      .then(stream => {
        // Stop the stream immediately, we just needed the permissions
        stream.getTracks().forEach(track => track.stop());
        // Now load the cameras
        loadCameras();
      })
      .catch(error => {
        console.error("Error requesting camera permission:", error);
        setScannerError(true);
        setNotification({
          type: 'error',
          message: 'Error al acceder a la cámara. Verifica los permisos del navegador.'
        });
      });
  }, []);
  
  // Auto-clear notifications after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [notification]);
  
  // Initialize and handle QR scanner
  useEffect(() => {
    let html5QrCode = null;

    const startScanner = async () => {
      if (isCameraActive && selectedCamera && scannerContainerRef.current) {
        try {
          // Clear previous scanner error
          setScannerError(false);
          
          // Create instance of scanner
          html5QrCode = new Html5Qrcode("scanner-container");
          
          // Config for scanner - optimized for barcode scanning
          const config = {
            fps: 15,
            qrbox: { width: 300, height: 150 }, // Better for barcode (wider than tall)
            aspectRatio: window.innerWidth > window.innerHeight ? 1.77 : 0.56, // Adjust based on orientation
            // No especificar formatsToSupport para usar los valores por defecto
            experimentalFeatures: {
              useBarCodeDetectorIfSupported: true
            },
            forceImageCapture: true  // Better for barcodes on challenging surfaces
          };
          
          // Start scanning
          await html5QrCode.start(
            selectedCamera,
            config,
            (decodedText) => {
              console.log(`Barcode/QR detected: ${decodedText}`);
              
              // Play scan detection sound
              playSound('scan');
              
              // Vibrate if available (mobile devices)
              if (navigator.vibrate) {
                navigator.vibrate(50);
              }
              
              // QR code detected - set tracking number
              setTrackingNumber(decodedText);
              
              // Create a submission to process the code
              const processScannedCode = async () => {
                try {
                  // Add visual feedback that code is being processed
                  setNotification({
                    type: 'info',
                    message: `Procesando: ${decodedText}`
                  });
                  
                  await handleSubmitTrackingNumber();
                  // Successful scan will cause a pause to prevent duplicate scans
                  await new Promise(resolve => setTimeout(resolve, 2000));
                } catch (error) {
                  console.error("Error processing scanned tracking number:", error);
                }
              };
              
              processScannedCode();
            },
            (errorMessage) => {
              // Log the error for debugging
              console.debug(`QR Code scanning error: ${errorMessage}`);
              
              // No establecemos el estado de error aquí para evitar interrupciones continuas
              // durante el escaneo normal, ya que estos errores pueden ser temporales
            }
          );
          
          // Store scanner reference
          scannerRef.current = html5QrCode;
        } catch (error) {
          console.error("Error initializing scanner:", error);
          setScannerError(true);
          scannerRef.current = null;
          setNotification({
            type: 'error',
            message: `Error al inicializar escáner: ${error.message || 'Problema al acceder a la cámara'}`
          });
        }
      }
    };

    // Start scanner if camera is active
    if (isCameraActive) {
      startScanner();
    }
    
    // Cleanup: stop scanner when camera is deactivated
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop()
          .catch(error => console.error("Error stopping scanner:", error))
          .finally(() => {
            scannerRef.current = null;
          });
      }
    };
  }, [isCameraActive, selectedCamera]);
  
  // Load batch data when currentBatch changes
  useEffect(() => {
    const loadBatchData = async () => {
      if (currentBatch) {
        try {
          const scans = await getBatchScans(currentBatch.id);
          setScanResults(scans);
          
          // Update stats
          setStats({
            totalScans: scans.length,
            successfulScans: scans.filter(scan => scan.status === 'success').length,
            errorScans: scans.filter(scan => scan.status === 'error').length
          });
        } catch (error) {
          console.error("Error loading batch data:", error);
        }
      }
    };
    
    loadBatchData();
  }, [currentBatch]);
  
  // Función para crear un nuevo lote con nombre automático
  const handleCreateNewBatch = async () => {
    if (isCreatingBatch) return;
    
    setIsCreatingBatch(true);
    
    // Crear un nombre de lote basado en la fecha y hora actual
    const now = new Date();
    const formattedDate = now.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');
    
    const formattedTime = now.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    }).replace(/:/g, '-');
    
    const batchName = `Entregas_${formattedDate}_${formattedTime}`;
    
    try {
      const batch = await createDeliveryScanBatch(batchName);
      setCurrentBatch(batch);
      setIsCreatingBatch(false);
      
      // Mostrar notificación
      setNotification({
        type: 'success',
        message: `Nuevo lote creado: ${batchName}`
      });
    } catch (error) {
      console.error("Error creating batch:", error);
      setIsCreatingBatch(false);
      
      // Mostrar error
      setNotification({
        type: 'error',
        message: `Error al crear lote: ${error.message}`
      });
    }
  };
  
  const handleSubmitTrackingNumber = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    
    const currentTrackingNumber = trackingNumber.trim();
    if (!currentTrackingNumber || isProcessing || !currentBatch) return;
    
    setIsProcessing(true);
    
    try {
      console.log(`Procesando número de seguimiento: ${currentTrackingNumber}`);
      
      // Find shipment in Odoo
      // Buscar el envío en Odoo - los errores serán capturados por el bloque try/catch principal
      const shipment = await findShipmentByTrackingNumber(currentTrackingNumber);
      
      // Update pickup datetime in Odoo
      const now = new Date().toISOString();
      await updateShipmentPickupDatetime(shipment.id, now);
      
      console.log(`Fecha de recogida actualizada para el envío: ${shipment.id}`);
      
      // Show notification
      setNotification({
        type: 'success',
        message: `Actualizado correctamente: ${currentTrackingNumber}`
      });
      
      // Upload images if any were captured
      let imageUrls = [];
      if (capturedImages.length > 0) {
        try {
          // Convert base64 images to blobs
          const imageBlobs = await Promise.all(capturedImages.map(async (img) => {
            const response = await fetch(img.url);
            const blob = await response.blob();
            return new File([blob], img.name || `delivery_${Date.now()}.jpg`, { type: img.type || 'image/jpeg' });
          }));
          
          // Upload to Supabase storage
          imageUrls = await uploadMultipleImages(imageBlobs, 'qrlabel-images', `deliveries/${currentBatch.id}`);
        } catch (uploadError) {
          console.error('Error uploading images:', uploadError);
          // Continue with scan even if image upload fails
        }
      }
      
      // Record successful scan with images
      const scanResult = await recordBatchScan(
        {
          tracking_number: currentTrackingNumber,
          status: 'success',
          odoo_shipment_id: shipment.id,
          images: imageUrls
        },
        currentBatch.id
      );
      
      console.log(`Escaneo registrado correctamente en la base de datos`);
      
      // Update UI
      setScanResults(prev => [scanResult.scan, ...prev]);
      setStats(prev => ({
        totalScans: prev.totalScans + 1,
        successfulScans: prev.successfulScans + 1,
        errorScans: prev.errorScans
      }));
      
      // Play success sound
      playSound('success');
    } catch (error) {
      console.error("Error processing tracking number:", error);
      
      // Set a user-friendly error message
      let errorMessage = error.message || 'Error desconocido';
      
      // Identificar el tipo de error y asignar un mensaje claro y directo
      if (errorMessage.includes('CORS') || 
          errorMessage.includes('Failed to fetch') || 
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('Error de conexión a API')) {
        
        errorMessage = 'Error de conexión con el servidor. Contacte con el administrador.';
      } else if (error.name === 'TypeError' && errorMessage.includes('fetch')) {
        errorMessage = 'Error de red: Compruebe su conexión a Internet';
      } else if (errorMessage.includes('Tracking no encontrado') || 
                errorMessage.includes('No shipment found')) {
        errorMessage = `Tracking no encontrado: ${currentTrackingNumber}`;
      } else if (errorMessage.includes('404')) {
        errorMessage = 'Servidor no disponible. Contacte con el administrador.';
      } else if (errorMessage.includes('401') || errorMessage.includes('403') || 
                errorMessage.includes('Unauthorized') || errorMessage.includes('Access denied')) {
        errorMessage = 'Error de autorización. Contacte con el administrador.';
      } else if (errorMessage.includes('500')) {
        errorMessage = 'Error interno del servidor. Contacte con el administrador.';
      } else if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
        errorMessage = 'Tiempo de espera agotado. Intente de nuevo.';
      } else {
        // Para errores no específicos, mostramos un mensaje genérico
        errorMessage = 'Error al procesar la solicitud. Intente de nuevo.';
      }
      
      // Show error notification with user-friendly message
      setNotification({
        type: 'error',
        message: errorMessage
      });
      
      // Record error scan
      try {
        const scanResult = await recordBatchScan(
          {
            tracking_number: currentTrackingNumber,
            status: 'error',
            error_message: errorMessage
          },
          currentBatch.id
        );
        
        // Update UI
        setScanResults(prev => [scanResult.scan, ...prev]);
        setStats(prev => ({
          totalScans: prev.totalScans + 1,
          successfulScans: prev.successfulScans,
          errorScans: prev.errorScans + 1
        }));
        
        // Play error sound
        playSound('error');
      } catch (recordError) {
        console.error("Error recording scan:", recordError);
        
        // If we can't record the scan, at least show an error
        setNotification({
          type: 'error',
          message: `Error al guardar el registro: ${recordError.message || 'Error de base de datos'}`
        });
      }
    } finally {
      setTrackingNumber('');
      setCapturedImages([]); // Clear images after processing
      setIsProcessing(false);
    }
  };
  
  const handleCameraToggle = () => {
    // Toggle camera on/off
    if (isCameraActive) {
      // Desactivar primero la bandera para evitar problemas de estado
      setIsCameraActive(false);
      
      // Luego detenemos el escáner si existe
      if (scannerRef.current) {
        scannerRef.current.stop()
          .catch(error => console.error("Error stopping scanner:", error))
          .finally(() => {
            scannerRef.current = null;
            // La bandera ya fue desactivada arriba
          });
      }
      
      // Notificar al usuario
      setNotification({
        type: 'info',
        message: 'Cámara desactivada'
      });
    } else {
      // Restablecer estado y notificar
      setScannerError(false);
      setIsCameraActive(true);
      
      setNotification({
        type: 'info',
        message: 'Activando cámara...'
      });
    }
  };
  
  const handleCameraChange = (event) => {
    const newCameraId = event.target.value;
    
    // Mostrar mensaje de cambio de cámara
    setNotification({
      type: 'info',
      message: 'Cambiando cámara...'
    });
    
    // Stop current scanner if active
    if (isCameraActive && scannerRef.current) {
      // Primero establecemos cameraActive a false para que se desmonte correctamente
      setIsCameraActive(false);
      
      // Luego detenemos el escáner actual
      scannerRef.current.stop()
        .catch(error => console.error("Error stopping scanner:", error))
        .finally(() => {
          scannerRef.current = null;
          
          // Update camera 
          setSelectedCamera(newCameraId);
          console.log(`Camera changed to: ${newCameraId}`);
          
          // Esperamos un momento y reactivamos la cámara
          setTimeout(() => {
            setScannerError(false);
            setIsCameraActive(true);
          }, 500);
        });
    } else {
      // Just update selected camera if scanner is not active
      setSelectedCamera(newCameraId);
      console.log(`Camera selected: ${newCameraId}`);
    }
  };
  
  const handleFinishBatch = () => {
    // Mostrar mensaje de carga
    setIsCreatingBatch(true);
    setNotification({
      type: 'info',
      message: 'Creando nuevo lote...'
    });
    
    // Reiniciar los estados y crear automáticamente un nuevo lote
    setCurrentBatch(null);
    setScanResults([]);
    setStats({
      totalScans: 0,
      successfulScans: 0,
      errorScans: 0
    });
    
    // Un pequeño retraso para asegurar que el estado se actualice primero
    setTimeout(() => handleCreateNewBatch(), 100);
  };
  
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };
  
  const playSound = (type) => {
    // Simple browser beep with better sound configuration
    const context = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    
    oscillator.connect(gain);
    gain.connect(context.destination);
    
    if (type === 'success') {
      // More pleasant success sound - higher pitch with quick ramp up/down
      oscillator.type = 'sine';
      oscillator.frequency.value = 1200;
      gain.gain.value = 0.2;
      
      // Ramp up and down for smoother sound
      gain.gain.setValueAtTime(0, context.currentTime);
      gain.gain.linearRampToValueAtTime(0.2, context.currentTime + 0.05);
      gain.gain.linearRampToValueAtTime(0, context.currentTime + 0.15);
      
      oscillator.start(context.currentTime);
      oscillator.stop(context.currentTime + 0.15);
    } else if (type === 'error') {
      // Two-tone error sound
      oscillator.type = 'square';
      oscillator.frequency.value = 350;
      gain.gain.value = 0.15;
      
      oscillator.start(context.currentTime);
      
      // Frequency shift for two-tone effect
      oscillator.frequency.setValueAtTime(350, context.currentTime);
      oscillator.frequency.setValueAtTime(250, context.currentTime + 0.15);
      
      // Envelope for smoother sound
      gain.gain.setValueAtTime(0, context.currentTime);
      gain.gain.linearRampToValueAtTime(0.15, context.currentTime + 0.05);
      gain.gain.linearRampToValueAtTime(0.15, context.currentTime + 0.15);
      gain.gain.linearRampToValueAtTime(0.15, context.currentTime + 0.2);
      gain.gain.linearRampToValueAtTime(0, context.currentTime + 0.3);
      
      oscillator.stop(context.currentTime + 0.3);
    } else if (type === 'scan') {
      // Short click for scan detection
      oscillator.type = 'sine';
      oscillator.frequency.value = 800;
      gain.gain.value = 0.1;
      
      gain.gain.setValueAtTime(0, context.currentTime);
      gain.gain.linearRampToValueAtTime(0.1, context.currentTime + 0.01);
      gain.gain.linearRampToValueAtTime(0, context.currentTime + 0.05);
      
      oscillator.start(context.currentTime);
      oscillator.stop(context.currentTime + 0.05);
    }
  };
  
  return (
    <div className="delivery-tracker">
      {/* Notification component */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
      
      {/* Batch management */}
      {currentBatch && (
        <div className="batch-controls">
          <div className="batch-info">
            <h3>Lote: {currentBatch.batch_name}</h3>
            <p>Iniciado: {new Date(currentBatch.created_at).toLocaleDateString('es-ES')}</p>
          </div>
          <div className="button-group">
            <button 
              className="secondary-button" 
              onClick={handleFinishBatch}
            >
              Finalizar y Crear Nuevo Lote
            </button>
          </div>
        </div>
      )}
      
      {/* Scanner section */}
      <div className="scanner-container">
        {isCameraActive && (
          <div className="camera-container">
            <div id="scanner-container" ref={scannerContainerRef}></div>
            {!scannerError && <div className="scanner-active-indicator"></div>}
          </div>
        )}
        
        {scannerError && isCameraActive && (
          <div className="camera-error-message">
            <p>No se pudo inicializar el escáner de código QR. Por favor, utiliza la entrada manual.</p>
          </div>
        )}
        
        <div className="scanner-options">
          <button 
            className={isCameraActive ? "secondary-button" : "primary-button"} 
            onClick={handleCameraToggle}
            disabled={isProcessing || !currentBatch}
          >
            {isCameraActive ? "Desactivar Cámara" : "Activar Cámara"}
          </button>
          
          {availableCameras.length > 0 && (
            <select 
              value={selectedCamera} 
              onChange={handleCameraChange}
              disabled={isProcessing || !currentBatch}
            >
              {availableCameras.map(camera => (
                <option key={camera.deviceId} value={camera.deviceId}>
                  {camera.label || `Cámara ${camera.deviceId.substr(0, 5)}`}
                </option>
              ))}
            </select>
          )}
        </div>
        
        <form onSubmit={handleSubmitTrackingNumber} className="tracking-input">
          <input
            type="text"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder="Escanea o ingresa número de seguimiento"
            disabled={isProcessing || !currentBatch}
          />
          <button 
            type="submit" 
            disabled={!trackingNumber.trim() || isProcessing || !currentBatch}
          >
            {isProcessing ? <span className="spinner"></span> : "Registrar"}
          </button>
        </form>
        
        {/* Image capture component */}
        <div className="image-capture-section">
          <h3>Capturar fotos del paquete (opcional)</h3>
          <ImageCapture 
            onImageCapture={(images) => setCapturedImages(images)}
          />
        </div>
      </div>
      
      {/* Stats section */}
      <div className="stats-container">
        <div className="stat-box">
          <h3>Total Escaneados</h3>
          <p>{stats.totalScans}</p>
        </div>
        <div className="stat-box">
          <h3>Exitosos</h3>
          <p>{stats.successfulScans}</p>
        </div>
        <div className="stat-box">
          <h3>Errores</h3>
          <p>{stats.errorScans}</p>
        </div>
      </div>
      
      {/* Results section */}
      <div className="scan-results">
        <h2>Resultados de Escaneo</h2>
        {scanResults.length === 0 ? (
          <p>No hay escaneos registrados.</p>
        ) : (
          <div className="result-list">
            {scanResults.map(result => (
              <div key={result.id} className={`result-item ${result.status}`}>
                <span className="result-tracking">{result.tracking_number}</span>
                <span className="result-message">
                  {result.status === 'success' 
                    ? `Actualizado ID: ${result.odoo_shipment_id || 'N/A'}` 
                    : result.error_message}
                  {result.images && result.images.length > 0 && (
                    <span className="image-indicator"> 📷 {result.images.length}</span>
                  )}
                </span>
                <span className="result-time">{formatTime(result.scanned_at)}</span>
                <span className={`status-indicator ${result.status === 'success' ? 'status-success' : 'status-error'}`}>
                  {result.status === 'success' ? 'OK' : 'Error'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Indicador de carga al crear lote automáticamente */}
      {isCreatingBatch && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Creando lote automáticamente...</p>
        </div>
      )}
    </div>
  );
};

export default DeliveryTracker;